import { MantineProvider, v8CssVariablesResolver, type CSSVariablesResolver } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeContext } from './useTheme';
import { getBundle } from './index';
import type { ThemeKey } from './types';

// Mantine 9 made `variant="light"` solid by default (was tinted/transparent in v8).
// Apply v8CssVariablesResolver to keep v8 light-variant colors across ~395 callsites.
// If a theme bundle already supplies its own resolver, compose them: v8 resolver first,
// bundle resolver merges on top so theme-specific overrides (e.g. editorial's
// --mantine-color-body) win.
function composeResolvers(extra?: CSSVariablesResolver): CSSVariablesResolver {
  if (!extra) return v8CssVariablesResolver;
  return (theme) => {
    const base = v8CssVariablesResolver(theme);
    const overlay = extra(theme);
    return {
      variables: { ...base.variables, ...overlay.variables },
      light: { ...base.light, ...overlay.light },
      dark: { ...base.dark, ...overlay.dark },
    };
  };
}

const STORAGE_KEY = 'tt-theme';
const QUERY_PARAM = 'tt-theme';

function isThemeKey(value: unknown): value is ThemeKey {
  return value === 'baseline' || value === 'editorial';
}

function readInitialThemeKey(): ThemeKey {
  if (typeof window === 'undefined') return 'baseline';

  // Query-param wins and is persisted to localStorage so reload keeps the choice.
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get(QUERY_PARAM);
  if (isThemeKey(fromQuery)) {
    try {
      window.localStorage.setItem(STORAGE_KEY, fromQuery);
    } catch {
      // localStorage may be unavailable (private mode, quota) — fall through.
    }
    return fromQuery;
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeKey(stored)) return stored;
  } catch {
    // localStorage may be unavailable.
  }

  return 'baseline';
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeKey, _setThemeKey] = useState<ThemeKey>(readInitialThemeKey);
  const bundle = getBundle(themeKey);
  const cssVariablesResolver = useMemo(
    () => composeResolvers(bundle.cssVariablesResolver),
    [bundle.cssVariablesResolver],
  );

  // Sync body attribute + persistence + lazy CSS load on every theme change.
  useEffect(() => {
    document.body.setAttribute('data-tt-theme', themeKey);
    try {
      window.localStorage.setItem(STORAGE_KEY, themeKey);
    } catch {
      // Ignore storage errors — runtime state is still correct.
    }
    // Editorial bundle attaches CSS chunks lazily; baseline is a no-op.
    void bundle.loadStyles?.();
  }, [themeKey, bundle]);

  const setThemeKey = useCallback((key: ThemeKey) => _setThemeKey(key), []);

  return (
    <ThemeContext.Provider value={{ themeKey, setThemeKey }}>
      <MantineProvider
        theme={bundle.theme}
        cssVariablesResolver={cssVariablesResolver}
        defaultColorScheme="light"
      >
        {bundle.Chrome ? <bundle.Chrome /> : null}
        {children}
      </MantineProvider>
    </ThemeContext.Provider>
  );
}
